import { useParams } from 'react-router-dom';
import useFundIdFromPath from '@/hooks/useFundIdFromPath';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FundMyAccessDtoOut, PermissionWorld } from '@/models/out/FundPersonDtoOut';
import { FULL_WORLDS, atLeast, levelIn, worldOfPath } from '@/helpers/permissionWorlds';

export const fundPeopleUrl = (fundId: number | string) => `funds/${fundId}/people`;
export const myFundAccessUrl = (fundId: number | string) => `funds/${fundId}/people/me`;

/**
 * What the signed-in coordinator may do in a fund (15.10.2026), world by world since 22.10.2026:
 * screens hide a closed world and lock the actions of one that may only be seen, instead of
 * letting a coordinator fill a form that fails at the end. While loading, or if the call fails,
 * everything reads as allowed - the server still refuses what isn't, so a slow answer never locks
 * anyone out. Asked only while «רכזים והרשאות»'s flag is on: the endpoint is behind it, and with
 * it off every screen behaves as it did before.
 */
const useFundAccess = (fundIdOverride?: number) => {
  const { fundId: fundIdParam } = useParams();
  // The header and the pollers sit outside the fund's route, where only the address names the fund.
  const fundIdInPath = useFundIdFromPath();
  const fundIdText = fundIdParam ?? fundIdInPath;
  const fundId = fundIdOverride ?? (fundIdText ? Number(fundIdText) : undefined);
  const isPeopleOn = useFeature('fund-representatives');
  const { data, isLoading } = useGetAuthApi<FundMyAccessDtoOut>(isPeopleOn && fundId ? myFundAccessUrl(fundId) : '');

  const worlds = data?.worlds ?? FULL_WORLDS;
  const canView = (world: PermissionWorld) => atLeast(levelIn(worlds, world), 'View');
  const canEdit = (world: PermissionWorld) => atLeast(levelIn(worlds, world), 'Edit');

  return {
    access: data,
    isLoading,
    canWrite: data?.canWrite ?? true,
    isViewOnly: data != null && !data.canWrite,
    canManagePeople: data?.canManagePeople ?? false,
    worlds,
    canView,
    canEdit,
    /** A screen of the fund, by its path: hidden when its world is closed. */
    canOpen: (to: string) => {
      const world = worldOfPath(to);
      return world == null || canView(world);
    },
  };
};

export default useFundAccess;
